import { useEffect, useCallback, useRef } from 'react';
import { useChainManager } from './useChainManager';
import { SupportedChain } from '../../constants';

/**
 * Generic hook for subscribing to blockchain events
 * Works across all chains (BNB, Solana, etc.)
 */
export function useBlockchainEvent<T = any>(
  eventName: string,
  handler: (data: T) => void,
  options?: {
    contractAddress?: string;
    filterArgs?: any[];
    enabled?: boolean;
  },
) {
  const { chainManager, currentChain } = useChainManager();
  const handlerRef = useRef(handler);
  const enabled = options?.enabled !== false;

  // Update handler ref when it changes
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const adapter = chainManager.getCurrentAdapter();

    // Check if adapter supports generic event subscription
    if (!adapter.subscribeToEvent) {
      console.warn(
        `Adapter for ${currentChain} does not support subscribeToEvent for ${eventName}`,
      );
      return;
    }

    // Create a wrapper handler that calls the latest handler
    const wrapperHandler = (data: T) => {
      handlerRef.current(data);
    };

    // Subscribe to event
    const unsubscribe = adapter.subscribeToEvent(eventName, wrapperHandler, {
      address: options?.contractAddress,
      filterArgs: options?.filterArgs,
    });

    return () => {
      unsubscribe();
    };
  }, [
    eventName,
    enabled,
    options?.contractAddress,
    JSON.stringify(options?.filterArgs),
    currentChain,
    chainManager,
  ]);
}

/**
 * Hook specifically for MintLixi events
 * Works across all chains
 */
export function useMintLixiEvent(
  handler: (data: {
    owner: string;
    groupId: string;
    numTickets: number;
    startTicketId: string;
    purchaseDate?: number;
    globalJackpotAdded?: string;
  }) => void,
  options?: {
    contractAddress?: string;
    enabled?: boolean;
  },
) {
  const { currentChain } = useChainManager();

  // Chain-specific event handling
  const handleEvent = useCallback(
    (data: any) => {
      // Adapt event data based on chain
      if (currentChain === 'bnb') {
        // EVM event format
        handler({
          owner: data.owner || data[0],
          groupId: data.groupId?.toString() || data[1]?.toString(),
          numTickets: data.numTickets?.toNumber() || data[2]?.toNumber() || 0,
          startTicketId: data.startTicketId?.toString() || data[3]?.toString(),
          purchaseDate: data.purchaseDate?.toNumber(),
          globalJackpotAdded: data.globalJackpotAdded?.toString(),
        });
      } else if (currentChain === 'solana') {
        // Solana event format (would need to be implemented in SolanaAdapter)
        handler({
          owner: data.owner?.toString(),
          groupId: data.groupId?.toString(),
          numTickets: data.numTickets || 0,
          startTicketId: data.startTicketId?.toString(),
          purchaseDate: data.purchaseDate,
          globalJackpotAdded: data.globalJackpotAdded?.toString(),
        });
      }
    },
    [currentChain, handler],
  );

  useBlockchainEvent('MintLixi', handleEvent, {
    contractAddress: options?.contractAddress,
    enabled: options?.enabled,
  });
}
